import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useMedia } from "@rapid/contexts/MediaContext";
import { useFileCarouselThumbnails } from "@rapid/hooks/useFileCarousel";
import { useFrameStable } from "@rapid/contexts/FrameContext";
import { MediaItem, MediaItemStatus } from "@rapid/types/media";
import { Annotation, AnnotationSource, isAnnotationVisible } from "@rapid/types/annotation";
import {
    InferenceStatus,
    useAnnotationsForFileAndFrameId,
    useInferenceQueue,
    usePromptLabels,
    useUserAnnotationsSnapshot
} from "@rapid/store";
import { getLabelColor } from "@rapid/components/annotation/colors";
import { getAllAnnotationsForItem } from "@rapid/utils/resolveAnnotations";
import Button from "@scripts/react/components/Button";
import { TooltipContainer } from "@scripts/react/components/Tooltip";

function useOriginalImageDimensions(mediaItems: MediaItem[]) {
    const [dims, setDims] = useState<Record<string, { w: number; h: number }>>({});
    const loadedIds = useRef(new Set<string>());

    useEffect(() => {
        let cancelled = false;

        function loadDimension(item: MediaItem) {
            const url = URL.createObjectURL(item.file);
            const img = new Image();
            img.onload = () => {
                if (!cancelled) {
                    loadedIds.current.add(item.id);
                    setDims((p) => ({
                        ...p,
                        [item.id]: { w: img.naturalWidth, h: img.naturalHeight }
                    }));
                }
                URL.revokeObjectURL(url);
            };
            img.onerror = () => URL.revokeObjectURL(url);
            img.src = url;
        }

        for (const item of mediaItems) {
            if (item.isVideo || loadedIds.current.has(item.id)) continue;
            loadDimension(item);
        }

        return () => {
            cancelled = true;
        };
    }, [mediaItems]);

    return dims;
}

type ReviewGridViewProps = {
    onSelectMedia: (mediaItem: MediaItem) => void;
    visible: boolean;
    annotationSource: AnnotationSource;
    orderedMediaItemsOverride?: MediaItem[];
    flattenedVideoSourceId?: string | null;
};

export function ReviewGridView({
    onSelectMedia,
    visible,
    annotationSource,
    orderedMediaItemsOverride,
    flattenedVideoSourceId
}: ReviewGridViewProps) {
    const { orderedMediaItems: orderedMediaItemsFromContext } = useMedia();
    const orderedMediaItems = orderedMediaItemsOverride ?? orderedMediaItemsFromContext;
    const { orderedTimelineFramePreviewUrls } = useFrameStable();
    const thumbnails = useFileCarouselThumbnails(
        orderedMediaItems,
        orderedTimelineFramePreviewUrls,
        true
    );
    const liveAnnotations = useAnnotationsForFileAndFrameId();
    const snapshotAnnotations = useUserAnnotationsSnapshot();
    const allAnnotations =
        annotationSource === AnnotationSource.Annotations ? snapshotAnnotations : liveAnnotations;
    const inferenceQueue = useInferenceQueue();
    const promptLabels = usePromptLabels();
    const originalDims = useOriginalImageDimensions(orderedMediaItems);

    return (
        <div
            className={`absolute inset-0 overflow-y-auto bg-gray-900 p-2 ${visible ? "" : "hidden"}`}
        >
            <div className="grid grid-cols-3 gap-2">
                {orderedMediaItems.map((mediaItem) => {
                    const itemAnnotations = getAllAnnotationsForItem(
                        allAnnotations,
                        mediaItem.id,
                        flattenedVideoSourceId ?? null
                    );
                    const visibleAnnotations = itemAnnotations.filter((a) =>
                        isAnnotationVisible(a, promptLabels)
                    );

                    const queueItem = inferenceQueue.get(mediaItem.id);
                    const isInferring =
                        queueItem?.status === InferenceStatus.QUEUED ||
                        queueItem?.status === InferenceStatus.INFERRING;

                    return (
                        <GridThumbnail
                            key={mediaItem.id}
                            mediaItem={mediaItem}
                            thumbnailUrl={thumbnails[mediaItem.id]}
                            annotations={visibleAnnotations}
                            originalDims={originalDims[mediaItem.id]}
                            promptLabels={promptLabels}
                            isInferring={isInferring}
                            onSelect={onSelectMedia}
                        />
                    );
                })}
            </div>
        </div>
    );
}

type ImageBounds = { left: number; top: number; width: number; height: number };

function computeContainedImageBounds(naturalWidth: number, naturalHeight: number): ImageBounds {
    const imageAspect = naturalWidth / naturalHeight;
    if (imageAspect > 1) {
        const renderedHeight = (1 / imageAspect) * 100;
        return { left: 0, top: (100 - renderedHeight) / 2, width: 100, height: renderedHeight };
    }
    const renderedWidth = imageAspect * 100;
    return { left: (100 - renderedWidth) / 2, top: 0, width: renderedWidth, height: 100 };
}

function GridThumbnail({
    mediaItem,
    thumbnailUrl,
    annotations,
    originalDims,
    promptLabels,
    isInferring,
    onSelect
}: {
    mediaItem: MediaItem;
    thumbnailUrl?: string;
    annotations: Annotation[];
    originalDims?: { w: number; h: number };
    promptLabels: Map<string, any>;
    isInferring: boolean;
    onSelect: (mediaItem: MediaItem) => void;
}) {
    const [isHovered, setIsHovered] = useState(false);

    const handleClick = useCallback(() => {
        onSelect(mediaItem);
    }, [mediaItem, onSelect]);

    const imageBounds = useMemo(
        () => (originalDims ? computeContainedImageBounds(originalDims.w, originalDims.h) : null),
        [originalDims]
    );

    const isUploading =
        mediaItem.status !== MediaItemStatus.COMPLETED &&
        mediaItem.status !== MediaItemStatus.ERROR;
    const isLoading = isUploading || isInferring;

    return (
        <div
            className="group relative w-full cursor-pointer overflow-hidden rounded-lg bg-black"
            style={{ paddingBottom: "100%" }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={handleClick}
        >
            {thumbnailUrl && (
                <img
                    src={thumbnailUrl}
                    alt={mediaItem.name}
                    className="absolute inset-0 h-full w-full object-contain"
                />
            )}

            {imageBounds &&
                originalDims &&
                annotations.map((annotation) => (
                    <BoxOverlayDiv
                        key={annotation.id}
                        annotation={annotation}
                        imageBounds={imageBounds}
                        originalWidth={originalDims.w}
                        originalHeight={originalDims.h}
                        color={getLabelColor(promptLabels, annotation.label)}
                    />
                ))}

            {isLoading && (
                <div
                    className={`absolute inset-0 flex items-center justify-center ${thumbnailUrl ? "bg-black/40" : "bg-gray-800"}`}
                >
                    <i className="fas fa-spinner fa-spin text-lg text-white" />
                </div>
            )}

            {isHovered && !isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <TooltipContainer
                        title={mediaItem.isVideo ? "View video" : "View image"}
                        type="titleOnly"
                        customButton={
                            <span>
                                <Button
                                    type="dark-secondary"
                                    size="xsmall"
                                    shape="square"
                                    className="pointer-events-none border-opacity-50"
                                >
                                    <i
                                        className={`fas ${mediaItem.isVideo ? "fa-play" : "fa-image"}`}
                                    />
                                </Button>
                            </span>
                        }
                    />
                </div>
            )}
        </div>
    );
}

function BoxOverlayDiv({
    annotation,
    imageBounds,
    originalWidth,
    originalHeight,
    color
}: {
    annotation: Pick<Annotation, "x" | "y" | "width" | "height">;
    imageBounds: ImageBounds;
    originalWidth: number;
    originalHeight: number;
    color: string;
}) {
    const leftNorm = (annotation.x - annotation.width / 2) / originalWidth;
    const topNorm = (annotation.y - annotation.height / 2) / originalHeight;
    const widthNorm = annotation.width / originalWidth;
    const heightNorm = annotation.height / originalHeight;

    return (
        <div
            className="pointer-events-none absolute rounded-sm border-2"
            style={{
                left: `${imageBounds.left + leftNorm * imageBounds.width}%`,
                top: `${imageBounds.top + topNorm * imageBounds.height}%`,
                width: `${widthNorm * imageBounds.width}%`,
                height: `${heightNorm * imageBounds.height}%`,
                borderColor: color
            }}
        />
    );
}
